import {
  Button,
  Column,
  Copy,
  Flex,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import React, {
  useCallback,
  useState,
  useEffect,
  FormEvent,
  useRef,
  ChangeEvent,
} from 'react';
import { debounce } from '@grubhubprod/umami-library/dist/nodash/debounce';
import { useParams, useHistory } from 'react-router-dom';
import { GroupTable } from 'src/components/People/Groups/GroupTable';
import { Input } from 'src/components/Input/Input';
import { ISearchParams, useGroupSearch } from 'src/utilities/useSearchHooks';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { StickyToolbarContainer } from 'src/components/People/StickyToolbarContainer';
import { useSearchToolbar } from 'src/components/People/Groups/hooks/useSearchToolbar';
import { Select } from 'src/components/Select/Select';
import { SortFields, SortOrder } from 'src/components/Table/SortableColumn';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { AddGroupForm } from './AddGroupForm';
import { GroupManagementModal } from './GroupManagementModal';

const DEBOUNCE_SEARCH_TIMING = 300;
const SORT_BY_OPTION = 'Sort by';
export const GroupsSortOptions = [
  {
    label: 'Name: A-Z',
    value: { field: SortFields.NAME, order: SortOrder.ASC },
  },
  {
    label: 'Name: Z-A',
    value: { field: SortFields.NAME, order: SortOrder.DESC },
  },
];

export default function GroupLanding(): JSX.Element {
  const { isPhone, isDesktop } = useScreenSizes();
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const { push } = useHistory();
  const currentSearchTermRef = useRef<string>('');
  const selectSortRef = useRef<HTMLSelectElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [sortField, setSortField] = useState(SortFields.NAME);
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC);

  const { sendAnalyticsBefore } = useSendAnalytics({
    eventaction: 'create group_impression',
    eventcategory: 'self-service management tool_people',
  });

  const { isToolbarVisible } = useSearchToolbar({
    callbackBeforeToggle: () => {
      currentSearchTermRef.current = searchInputRef.current?.value;
    },
    callbackAfterToggle: () => {
      if (searchInputRef.current)
        searchInputRef.current.value = currentSearchTermRef?.current || '';

      if (selectSortRef.current) {
        const option = GroupsSortOptions.find(
          (o) => o.value.order === sortOrder && o.value.field === sortField
        );

        selectSortRef.current.value = option ? option.label : '';
      }
    },
  });

  const [isAddGroupModalVisible, setIsAddGroupModalVisible] = useState(false);

  const handleCloseModal = useCallback(
    () => setIsAddGroupModalVisible(false),
    []
  );

  const {
    doSearch: getCorpGroups,
    resultsList: groups,
    totalResults,
    hasMoreResults,
    loadMoreNumber,
    hasNoResultsWithoutQuery,
    hasNoResultsWithQuery,
    searchStatus,
  } = useGroupSearch(corpId as string, true);

  useEffect(() => {
    if (corpId) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, corpId]);

  function doSearch(searchParams: ISearchParams = {}, isLoadingMore = false) {
    const query = searchInputRef.current?.value;
    const params: ISearchParams = { ...searchParams };

    if (query) {
      params.query = query;
    }

    if (sortOrder && sortField) {
      params.sort_order = sortOrder;
      params.sort_field = sortField;
    }

    return getCorpGroups(params, isLoadingMore);
  }

  const debouncedSearch = debounce(doSearch, DEBOUNCE_SEARCH_TIMING);

  function onSearchGroupInput(evt: FormEvent<HTMLInputElement>) {
    if (currentSearchTermRef.current) {
      currentSearchTermRef.current = evt.currentTarget.value;
    }
    debouncedSearch();
  }

  function onSortSelect(evt: ChangeEvent<HTMLSelectElement>) {
    const sortLabel = evt.target.value;
    const option = GroupsSortOptions.find(
      (option) => option.label === sortLabel
    );

    setSortField(option ? option.value.field : null);
    setSortOrder(option ? option.value.order : null);
  }

  function onSortColumn(field: SortFields, order: SortOrder) {
    setSortField(field);
    setSortOrder(order);
  }

  const onAddGroupModalSuccess = useCallback((groupId: string) => {
    handleCloseModal();
    push(`/corporate/management/${corpId}/groups/${groupId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddGroupClick = () => {
    setIsAddGroupModalVisible(true);
  };

  const searchBarElement = (
    <Flex
      justifyContent="space-between"
      data-testid="renderSearchBar"
      flexDirection={isPhone ? 'column-reverse' : 'row'}
      justifyItems="space-between"
    >
      <Column desktop={7}>
        <Flex
          alignItems="flex-end"
          justifyContent={isToolbarVisible ? 'space-between' : 'flex-start'}
          flexDirection={isPhone ? 'column' : 'row'}
        >
          <Column desktop={8} tablet={5}>
            <Stack y={isPhone ? 'xxx-sm' : 'none'} x={isPhone ? 'none' : 'md'}>
              <Input
                key="searchTermGroup"
                ariaLabel="Search by group name"
                placeholder="Search by group name"
                name="searchTermGroup"
                id="searchTermGroup"
                onInput={onSearchGroupInput}
                ref={searchInputRef}
                prefixIcon={
                  <Copy variant="body" color="secondaryText">
                    <SvgMagnifyingGlass size="16px" />
                  </Copy>
                }
              />
            </Stack>
          </Column>
          {!isDesktop && (
            <Column desktop={4} tablet={3}>
              <Stack y={!isToolbarVisible && isPhone ? 'x-sm' : 'none'}>
                <Select
                  name="sort"
                  id="sort"
                  onChange={onSortSelect}
                  ref={selectSortRef}
                  ariaLabel="Sort by"
                >
                  <option value="">{SORT_BY_OPTION}</option>
                  {GroupsSortOptions.map((option) => (
                    <option key={option.label} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Stack>
            </Column>
          )}
        </Flex>
      </Column>

      {!isToolbarVisible && (
        <Column tablet={3} phone={12}>
          <Stack y={isPhone ? 'lg' : 'none'}>
            <Button
              variant="primary"
              buttonSize="medium"
              block
              onClick={() => sendAnalyticsBefore(handleAddGroupClick)}
            >
              Add group
            </Button>
          </Stack>
        </Column>
      )}
    </Flex>
  );

  return (
    <>
      {isToolbarVisible ? (
        <StickyToolbarContainer>{searchBarElement}</StickyToolbarContainer>
      ) : (
        <Stack y={isPhone ? 'x-sm' : 'sm'}>{searchBarElement}</Stack>
      )}

      {!(isPhone && isToolbarVisible) && (
        <Stack y="x-sm">
          <Flex alignItems="flex-end" justifyContent="flex-end">
            <Copy variant="body" color="secondaryText">
              {`${groups.length} of ${totalResults}`}
            </Copy>
          </Flex>
        </Stack>
      )}

      <GroupTable
        getCorpGroups={doSearch}
        searchStatus={searchStatus}
        previousSortField={sortField}
        previousSortOrder={sortOrder}
        onSortColumnClick={onSortColumn}
        totalResults={totalResults}
        hasMoreResults={hasMoreResults}
        loadMoreGroupsSize={loadMoreNumber}
        groups={groups}
        hasNoResultsWithoutQuery={hasNoResultsWithoutQuery}
        hasNoResultsWithQuery={hasNoResultsWithQuery}
        currentQueryText={searchInputRef?.current?.value}
        bodyHeight="532px"
      />

      <GroupManagementModal
        isVisible={isAddGroupModalVisible}
        onClose={handleCloseModal}
        title="Add group"
      >
        <AddGroupForm
          corpId={corpId}
          onSuccess={onAddGroupModalSuccess}
          onCancel={handleCloseModal}
        />
      </GroupManagementModal>
    </>
  );
}
